import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { User, Session } from '@supabase/supabase-js';

interface AuthDebugState {
  user: User | null;
  session: Session | null;
  isReady: boolean;
  error: string | null;
  debugInfo: any;
}

export const useAuthDebug = () => {
  const [authState, setAuthState] = useState<AuthDebugState>({
    user: null,
    session: null,
    isReady: false,
    error: null,
    debugInfo: {}
  });

  useEffect(() => {
    console.log('=== useAuthDebug hook initialized ===');
    
    let timeoutId: ReturnType<typeof setTimeout>;
    
    const checkAuthState = async () => {
      try {
        console.log('Checking current auth state...');
        
        // Try to get the current session
        const { data: { session }, error } = await supabase.auth.getSession();
        
        // If no session but we have tokens in storage, try to refresh
        if (!session && !error) {
          console.log('No session found, attempting to refresh...');
          const { data: refreshData, error: refreshError } = await supabase.auth.refreshSession();
          
          if (refreshData.session) {
            console.log('Session refreshed successfully');
            setAuthState({
              user: refreshData.session.user,
              session: refreshData.session,
              isReady: true,
              error: null,
              debugInfo: {
                event: 'SESSION_REFRESHED',
                sessionExists: true,
                userId: refreshData.session.user.id,
                userEmail: refreshData.session.user.email,
                timestamp: new Date().toISOString()
              }
            });
            return;
          } else if (refreshError) {
            console.log('Session refresh failed:', refreshError.message);
          }
        }
        
        const debugInfo = {
          sessionExists: !!session,
          userId: session?.user?.id,
          userEmail: session?.user?.email,
          sessionExpiry: session?.expires_at,
          accessToken: session?.access_token ? 'present' : 'missing',
          refreshToken: session?.refresh_token ? 'present' : 'missing',
          timestamp: new Date().toISOString(),
          error: error?.message
        };
        
        console.log('Auth state check result:', debugInfo);
        
        setAuthState({
          user: session?.user || null,
          session: session,
          isReady: true,
          error: error?.message || null,
          debugInfo
        });
        
      } catch (err) {
        console.error('Error checking auth state:', err);
        setAuthState(prev => ({
          ...prev,
          isReady: true,
          error: 'Failed to check authentication state',
          debugInfo: { error: err }
        }));
      }
    };

    const setupAuthListener = () => {
      console.log('Setting up auth state listener...');
      
      const { data: { subscription } } = supabase.auth.onAuthStateChange(
        async (event, session) => {
          console.log('Auth state change detected:', { 
            event, 
            sessionExists: !!session,
            userId: session?.user?.id,
            userEmail: session?.user?.email 
          });
          
          const debugInfo = {
            event,
            sessionExists: !!session,
            userId: session?.user?.id,
            userEmail: session?.user?.email,
            sessionExpiry: session?.expires_at,
            timestamp: new Date().toISOString()
          };
          
          setAuthState({
            user: session?.user || null,
            session: session,
            isReady: true,
            error: null,
            debugInfo
          });
          
          // If we have a session, verify it's working with database
          if (session?.user) {
            setTimeout(async () => {
              try {
                console.log('Verifying session with database...');
                const { data, error } = await supabase.rpc('test_auth_context');
                
                console.log('Session verification result:', { data, error });
                
                if (error || !(data as any)?.auth_uid) {
                  console.error('Session verification failed:', error || 'No auth_uid returned');
                  setAuthState(prev => ({
                    ...prev,
                    error: 'Session verification failed - please log in again',
                    debugInfo: { 
                      ...prev.debugInfo, 
                      verificationError: error?.message || 'No auth_uid returned',
                      authContext: data
                    }
                  }));
                }
              } catch (verifyError) {
                console.error('Session verification error:', verifyError);
              }
            }, 1000);
          }
        }
      );

      return subscription;
    };
    
    // Set up auth listener first
    const subscription = setupAuthListener();
    
    // Then check current state after a short delay
    timeoutId = setTimeout(() => {
      checkAuthState();
    }, 100);

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      subscription.unsubscribe();
    };
  }, []);

  const refreshAuth = async () => {
    console.log('=== Manual auth refresh requested ===');
    try {
      const { data, error } = await supabase.auth.refreshSession();
      console.log('Auth refresh result:', { 
        success: !!data.session, 
        error: error?.message 
      });
      
      if (error) {
        setAuthState(prev => ({
          ...prev,
          error: 'Auth refresh failed: ' + error.message
        }));
      }
      
      return { data, error };
    } catch (err) {
      console.error('Auth refresh error:', err);
      return { data: null, error: err };
    }
  };

  return {
    ...authState,
    refreshAuth
  };
};